/**
 * INVI HQ — Insights dashboard. System-admin only (gated by view_insights at
 * the route). Shows tool usage, run outcomes, most active users, and recent
 * errors over a window, with an all-groups aggregate or a per-dealer-group
 * view. Reads /insights/summary via api/insights.ts.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react"
import {
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts"

import Layout from "../../components/Layout"
import InsightsHealthView from "./InsightsHealthView"
import { THEME } from "../../config/theme"
import { useDealerGroup } from "../../context/DealerGroupContext"
import {
  InsightsEventLogEntry,
  InsightsRange,
  InsightsSummary,
  InsightsUserUtility,
  fetchInsightsSummary,
  fetchInsightsUsers,
  fetchUserEvents,
} from "../../api/insights"

// Vendor ingestions run on a schedule, not by a person, so their tile sits
// with the headline figures; every other tool is something someone started.
const HEADLINE_TOOL = "ingestion"

// What an INVI HQ account shows where a dealer group's name would go.
const HEADQUARTERS_LABEL = "INVI HQ"

const DAY_RANGES = [
  { value: 7, label: "Last 7 days" },
  { value: 14, label: "Last 14 days" },
  { value: 30, label: "Last 30 days" },
  { value: 90, label: "Last 90 days" },
  { value: 180, label: "Last 180 days" },
  { value: 365, label: "Last 365 days" },
]

const CUSTOM_RANGE = "custom"

function InsightsDashboardPage() {
  return (
    <Layout>
      <InsightsDashboardContent />
    </Layout>
  )
}

function InsightsDashboardContent() {
  // Insights is a reporting surface, so it honors the global switcher including
  // the "All Groups" aggregate (dealerGroupId === null).
  const { dealerGroupId } = useDealerGroup()
  const [days, setDays] = useState<number>(30)
  const [rangeMode, setRangeMode] = useState<"preset" | "custom">("preset")
  const [customStart, setCustomStart] = useState("")
  const [customEnd, setCustomEnd] = useState("")
  const [view, setView] = useState<"usage" | "health">("usage")

  const [summary, setSummary] = useState<InsightsSummary | null>(null)
  const [users, setUsers] = useState<InsightsUserUtility[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Per-user drill-down (event-log lookup).
  const [userSearch, setUserSearch] = useState("")
  const [selectedUser, setSelectedUser] = useState<InsightsUserUtility | null>(null)
  const [userEvents, setUserEvents] = useState<InsightsEventLogEntry[]>([])
  const [eventsLoading, setEventsLoading] = useState(false)

  // The active window: a preset (last N days) or a complete custom date pair.
  // An incomplete custom pair falls back to the preset so we never query a
  // half-specified range.
  const range: InsightsRange = useMemo(
    () =>
      rangeMode === "custom" && customStart && customEnd
        ? { startDate: customStart, endDate: customEnd }
        : { days },
    [rangeMode, customStart, customEnd, days],
  )

  const rangeLabel =
    rangeMode === "custom" && customStart && customEnd
      ? `${customStart} → ${customEnd}`
      : `last ${days}d`

  const openUserDetail = useCallback(
    async (u: InsightsUserUtility) => {
      setSelectedUser(u)
      setUserEvents([])
      setEventsLoading(true)
      try {
        setUserEvents(await fetchUserEvents(u.user_id, range))
      } catch {
        setUserEvents([])
      } finally {
        setEventsLoading(false)
      }
    },
    [range],
  )

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [data, userRows] = await Promise.all([
        fetchInsightsSummary(range, dealerGroupId),
        fetchInsightsUsers(range, dealerGroupId),
      ])
      setSummary(data)
      setUsers(userRows)
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load insights")
    } finally {
      setLoading(false)
    }
  }, [dealerGroupId, range])

  useEffect(() => {
    // The Health tab owns its own data fetch; don't load the usage payload for it.
    if (view !== "usage") return
    let cancelled = false
    ;(async () => {
      setLoading(true)
      setError(null)
      try {
        const [data, userRows] = await Promise.all([
          fetchInsightsSummary(range, dealerGroupId),
          fetchInsightsUsers(range, dealerGroupId),
        ])
        if (!cancelled) {
          setSummary(data)
          setUsers(userRows)
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : "Failed to load insights")
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [dealerGroupId, range, view])

  // Flatten {date: {tool: n}} into recharts rows: {date, <tool>: n, ...}. The
  // tools come from the payload itself, in the backend's display order, so a
  // tool added there shows up as a line here with no edit on this side.
  const chartData = useMemo(() => {
    if (!summary) return []
    return summary.timeseries.map((point) => {
      const row: Record<string, number | string> = { date: point.date.slice(5) } // MM-DD
      for (const { tool } of summary.tool_usage) row[tool] = point.counts[tool] ?? 0
      return row
    })
  }, [summary])

  const outcomeData = useMemo(() => {
    if (!summary) return []
    return [
      { name: "Succeeded", value: summary.outcomes.success ?? 0, color: THEME.colors.semantic.success },
      { name: "Errored", value: summary.outcomes.failed ?? 0, color: THEME.colors.semantic.danger },
    ]
  }, [summary])

  const filteredUsers = useMemo(() => {
    const q = userSearch.trim().toLowerCase()
    if (!q) return users
    return users.filter(
      (u) => u.name.toLowerCase().includes(q) || u.username.toLowerCase().includes(q),
    )
  }, [users, userSearch])

  // -------------------------------------------------------------------------
  // Styles
  // -------------------------------------------------------------------------

  const selectStyle: React.CSSProperties = {
    height: "38px",
    border: `1px solid ${THEME.colors.silver}`,
    borderRadius: THEME.borderRadius.md,
    padding: "0 12px",
    fontSize: "14px",
    fontFamily: THEME.fonts.primary,
    color: THEME.colors.neutral.light.textPrimary,
    background: THEME.colors.neutral.light.surface,
    cursor: "pointer",
    outline: "none",
  }

  const panelStyle: React.CSSProperties = {
    background: THEME.colors.neutral.light.surface,
    border: `1px solid ${THEME.colors.silver}`,
    borderRadius: THEME.borderRadius.lg,
    padding: "20px",
  }

  const panelTitleStyle: React.CSSProperties = {
    margin: "0 0 16px 0",
    fontSize: "15px",
    fontWeight: 600,
    color: THEME.colors.neutral.light.textPrimary,
  }

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <>
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          marginBottom: "20px",
        }}
      >
        <div>
          <div
            style={{
              fontSize: THEME.typography.h3.size,
              fontWeight: THEME.typography.h3.weight,
              letterSpacing: THEME.typography.h3.tracking,
              color: THEME.colors.neutral.light.textPrimary,
            }}
          >
            Insights
          </div>
          <div
            style={{
              fontSize: "13px",
              color: THEME.colors.neutral.light.textSecondary,
              marginTop: "4px",
            }}
          >
            How INVI is being used across your dealer groups
          </div>
        </div>
        <div style={{ display: "flex", gap: "12px" }}>
          <select
            value={rangeMode === "custom" ? CUSTOM_RANGE : String(days)}
            onChange={(e) => {
              if (e.target.value === CUSTOM_RANGE) {
                setRangeMode("custom")
              } else {
                setRangeMode("preset")
                setDays(Number(e.target.value))
              }
            }}
            style={selectStyle}
          >
            {DAY_RANGES.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
            <option value={CUSTOM_RANGE}>Custom range…</option>
          </select>
          {rangeMode === "custom" && (
            <>
              <input
                type="date"
                value={customStart}
                max={customEnd || undefined}
                onChange={(e) => setCustomStart(e.target.value)}
                style={selectStyle}
                aria-label="Start date"
              />
              <span style={{ alignSelf: "center", color: THEME.colors.neutral.light.textSecondary }}>
                →
              </span>
              <input
                type="date"
                value={customEnd}
                min={customStart || undefined}
                onChange={(e) => setCustomEnd(e.target.value)}
                style={selectStyle}
                aria-label="End date"
              />
            </>
          )}
        </div>
      </div>

      <div
        style={{
          display: "flex",
          gap: "4px",
          borderBottom: `1px solid ${THEME.colors.silver}`,
          marginBottom: "20px",
        }}
      >
        {(["usage", "health"] as const).map((v) => (
          <button
            key={v}
            type="button"
            onClick={() => setView(v)}
            style={{
              padding: `${THEME.spacing.sm} ${THEME.spacing.md}`,
              fontSize: "14px",
              fontWeight: view === v ? 600 : 500,
              color: view === v ? THEME.colors.navy : THEME.colors.neutral.light.textSecondary,
              background: "none",
              border: "none",
              borderBottom: `2px solid ${view === v ? THEME.colors.navy : "transparent"}`,
              marginBottom: "-1px",
              cursor: "pointer",
              fontFamily: THEME.fonts.primary,
            }}
          >
            {v === "usage" ? "Usage" : "Health & reliability"}
          </button>
        ))}
      </div>

      {view === "health" ? (
        <InsightsHealthView range={range} dealerGroupId={dealerGroupId} />
      ) : (
        <>
      {error && (
        <div
          style={{
            background: "rgba(220, 38, 38, 0.08)",
            color: THEME.colors.semantic.danger,
            border: "0.5px solid rgba(220, 38, 38, 0.3)",
            padding: "10px 14px",
            borderRadius: THEME.borderRadius.md,
            marginBottom: "16px",
            fontSize: "13px",
          }}
        >
          {error}
          <button
            type="button"
            onClick={load}
            style={{
              marginLeft: "12px",
              background: "none",
              border: "none",
              color: THEME.colors.navy,
              cursor: "pointer",
              fontWeight: 500,
            }}
          >
            Retry
          </button>
        </div>
      )}

      {loading ? (
        <div
          style={{
            padding: "48px",
            textAlign: "center",
            color: THEME.colors.neutral.light.textSecondary,
          }}
        >
          Loading insights…
        </div>
      ) : !summary ? (
        <div
          style={{
            padding: "48px",
            textAlign: "center",
            color: THEME.colors.neutral.light.textSecondary,
          }}
        >
          No insights available.
        </div>
      ) : (
        <>
          {/* Headline numbers: the scheduled feed pulls, then the two figures
              about people. No "total runs" tile — the tool tiles below are
              that total's breakdown, so it only repeated them (Ian). */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
              gap: "16px",
              marginBottom: "16px",
            }}
          >
            {summary.tool_usage
              .filter((t) => t.tool === HEADLINE_TOOL)
              .map((t) => (
                <StatCard key={t.tool} label={t.label} value={t.count} />
              ))}
            <StatCard label="Active users" value={summary.totals.active_users ?? 0} />
            <StatCard
              label="Errors"
              value={summary.totals.errors ?? 0}
              accent={summary.totals.errors ? THEME.colors.semantic.danger : undefined}
            />
          </div>

          {/* One tile per tool a person starts, in the backend's display order.
              The tiles fill one row on a desktop and wrap as the window narrows
              (two across on a phone), rather than squeezing into one row. */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
              gap: "16px",
              marginBottom: "20px",
            }}
          >
            {summary.tool_usage
              .filter((t) => t.tool !== HEADLINE_TOOL)
              .map((t) => (
                <StatCard key={t.tool} label={t.label} value={t.count} />
              ))}
          </div>

          {/* Usage over time + outcomes */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "2fr 1fr",
              gap: "16px",
              marginBottom: "20px",
            }}
          >
            <div style={panelStyle}>
              <h2 style={panelTitleStyle}>Tool usage over time</h2>
              {chartData.length === 0 ? (
                <EmptyHint text="No runs in this window." />
              ) : (
                <ResponsiveContainer width="100%" height={240}>
                  <LineChart data={chartData} margin={{ top: 8, right: 16, bottom: 0, left: -16 }}>
                    <CartesianGrid stroke="#EEF0F3" vertical={false} />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 11, fill: THEME.colors.neutral.light.textSecondary }}
                      stroke={THEME.colors.silver}
                    />
                    <YAxis
                      allowDecimals={false}
                      tick={{ fontSize: 11, fill: THEME.colors.neutral.light.textSecondary }}
                      stroke={THEME.colors.silver}
                    />
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    {summary.tool_usage.map((t, idx) => (
                      <Line
                        key={t.tool}
                        type="monotone"
                        dataKey={t.tool}
                        name={t.label}
                        stroke={THEME.colors.chartSeries[idx % THEME.colors.chartSeries.length]}
                        // The palette has fewer colors than there are tools;
                        // a line that reuses a color is dashed so it still reads apart.
                        strokeDasharray={idx >= THEME.colors.chartSeries.length ? "6 3" : undefined}
                        strokeWidth={2}
                        dot={false}
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>

            <div style={panelStyle}>
              <h2 style={panelTitleStyle}>Run outcomes</h2>
              {outcomeData.every((d) => d.value === 0) ? (
                <EmptyHint text="No completed runs yet." />
              ) : (
                <>
                  <ResponsiveContainer width="100%" height={180}>
                    <PieChart>
                      <Pie
                        data={outcomeData}
                        dataKey="value"
                        nameKey="name"
                        innerRadius={48}
                        outerRadius={72}
                        paddingAngle={2}
                      >
                        {outcomeData.map((d) => (
                          <Cell key={d.name} fill={d.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      gap: "16px",
                      fontSize: "13px",
                      color: THEME.colors.neutral.light.textSecondary,
                    }}
                  >
                    <span>
                      <b style={{ color: THEME.colors.semantic.success }}>
                        {summary.outcomes.success ?? 0}
                      </b>{" "}
                      succeeded
                    </span>
                    <span>
                      <b style={{ color: THEME.colors.semantic.danger }}>
                        {summary.outcomes.failed ?? 0}
                      </b>{" "}
                      errored
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Active users + recent errors */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
            <div style={panelStyle}>
              <h2 style={panelTitleStyle}>Most visited pages</h2>
              {summary.most_visited_pages.length === 0 ? (
                <EmptyHint text="No page views in this window." />
              ) : (
                summary.most_visited_pages.map((p) => (
                  <div key={p.path} style={listRowStyle}>
                    <span style={{ fontFamily: THEME.fonts.mono, fontSize: "13px" }}>{p.path}</span>
                    <span style={{ fontWeight: 600 }}>{p.count}</span>
                  </div>
                ))
              )}
            </div>

            <div style={panelStyle}>
              <h2 style={panelTitleStyle}>Recent errors</h2>
              {summary.recent_errors.length === 0 ? (
                <EmptyHint text="No errors in this window. 🎉" />
              ) : (
                summary.recent_errors.map((e, idx) => (
                  <div key={idx} style={{ ...listRowStyle, display: "block" }}>
                    <div style={{ fontWeight: 500, fontSize: "13px" }}>{e.label}</div>
                    <div
                      style={{
                        fontSize: "12px",
                        color: THEME.colors.neutral.light.textSecondary,
                        marginTop: "2px",
                      }}
                    >
                      {e.dealer_group ? `${e.dealer_group} · ` : ""}
                      {new Date(e.occurred_at).toLocaleString("en-US", {
                        month: "short",
                        day: "numeric",
                        hour: "numeric",
                        minute: "2-digit",
                      })}
                      {e.message ? ` · ${e.message}` : ""}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Per-user utility — are individual key players actually getting value? */}
          <div style={{ ...panelStyle, marginTop: "20px" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: "16px",
              }}
            >
              <h2 style={{ ...panelTitleStyle, margin: 0 }}>
                User utility{" "}
                <span style={{ fontWeight: 400, fontSize: "12px", color: THEME.colors.neutral.light.textSecondary }}>
                  — click a user to see their event log
                </span>
              </h2>
              <input
                type="text"
                placeholder="Search users…"
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                style={{
                  height: "34px",
                  width: "200px",
                  border: `1px solid ${THEME.colors.silver}`,
                  borderRadius: THEME.borderRadius.md,
                  padding: "0 12px",
                  fontSize: "13px",
                  fontFamily: THEME.fonts.primary,
                  color: THEME.colors.neutral.light.textPrimary,
                  outline: "none",
                }}
              />
            </div>
            {filteredUsers.length === 0 ? (
              <EmptyHint text={userSearch ? "No users match your search." : "No users to show."} />
            ) : (
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px" }}>
                <thead>
                  <tr>
                    {["User", "Dealer group", "Last active", "Runs", "Pages", "Errors"].map((h, i) => (
                      <th
                        key={h}
                        style={{
                          textAlign: i >= 3 ? "right" : "left",
                          padding: "8px 10px",
                          fontSize: "11px",
                          letterSpacing: "0.5px",
                          textTransform: "uppercase",
                          color: THEME.colors.neutral.light.textSecondary,
                          borderBottom: `1px solid ${THEME.colors.silver}`,
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((u) => (
                    <tr
                      key={u.user_id}
                      onClick={() => openUserDetail(u)}
                      style={{ cursor: "pointer" }}
                    >
                      <td style={utilityCell}>
                        <div style={{ fontWeight: 500 }}>{u.name}</div>
                        <div style={{ fontSize: "12px", color: THEME.colors.neutral.light.textSecondary }}>
                          {u.username}
                        </div>
                      </td>
                      <td style={utilityCell}>
                        {u.dealer_group ?? (u.is_headquarters ? HEADQUARTERS_LABEL : "—")}
                      </td>
                      <td style={utilityCell}>
                        {u.last_active_at
                          ? new Date(u.last_active_at).toLocaleDateString("en-US", {
                              month: "short",
                              day: "numeric",
                              year: "numeric",
                            })
                          : "Never"}
                      </td>
                      <td style={{ ...utilityCell, textAlign: "right" }}>{u.runs}</td>
                      <td style={{ ...utilityCell, textAlign: "right" }}>{u.page_views}</td>
                      <td
                        style={{
                          ...utilityCell,
                          textAlign: "right",
                          color: u.errors ? THEME.colors.semantic.danger : THEME.colors.neutral.light.textPrimary,
                          fontWeight: u.errors ? 600 : 400,
                        }}
                      >
                        {u.errors}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {selectedUser && (
        <UserEventLogModal
          user={selectedUser}
          events={userEvents}
          loading={eventsLoading}
          rangeLabel={rangeLabel}
          onClose={() => setSelectedUser(null)}
        />
      )}
        </>
      )}
    </>
  )
}

// ---------------------------------------------------------------------------
// Small presentational helpers
// ---------------------------------------------------------------------------

interface StatCardProps {
  label: string
  value: number
  accent?: string
}

function StatCard({ label, value, accent }: StatCardProps) {
  return (
    <div
      style={{
        background: THEME.colors.neutral.light.surface,
        border: `1px solid ${THEME.colors.silver}`,
        borderRadius: THEME.borderRadius.lg,
        padding: "18px",
      }}
    >
      <div
        style={{
          fontSize: THEME.typography.caption.size,
          fontWeight: THEME.typography.caption.weight,
          letterSpacing: THEME.typography.caption.tracking,
          textTransform: "uppercase",
          color: THEME.colors.neutral.light.textSecondary,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: "30px",
          fontWeight: 600,
          marginTop: "8px",
          color: accent ?? THEME.colors.neutral.light.textPrimary,
        }}
      >
        {value.toLocaleString()}
      </div>
    </div>
  )
}

interface UserEventLogModalProps {
  user: InsightsUserUtility
  events: InsightsEventLogEntry[]
  loading: boolean
  rangeLabel: string
  onClose: () => void
}

// Per-event-type presentation. Errors read red; page views are neutral.
function eventStyle(type: string): { label: string; color: string } {
  if (type === "api_error") return { label: "API error", color: THEME.colors.semantic.danger }
  if (type === "client_error") return { label: "Frontend error", color: THEME.colors.semantic.danger }
  return { label: "Page view", color: THEME.colors.neutral.light.textSecondary }
}

function UserEventLogModal({ user, events, loading, rangeLabel, onClose }: UserEventLogModalProps) {
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.4)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "560px",
          maxWidth: "92vw",
          maxHeight: "85vh",
          display: "flex",
          flexDirection: "column",
          background: THEME.colors.neutral.light.surface,
          border: `1px solid ${THEME.colors.silver}`,
          borderRadius: THEME.borderRadius.lg,
          fontFamily: THEME.fonts.primary,
        }}
      >
        <div style={{ padding: "20px 24px", borderBottom: `1px solid ${THEME.colors.silver}` }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <div>
              <div style={{ fontSize: "18px", fontWeight: 600, color: THEME.colors.neutral.light.textPrimary }}>
                {user.name}
              </div>
              <div style={{ fontSize: "13px", color: THEME.colors.neutral.light.textSecondary }}>
                {user.username}
                {user.dealer_group
                  ? ` · ${user.dealer_group}`
                  : user.is_headquarters ? ` · ${HEADQUARTERS_LABEL}` : ""}
              </div>
            </div>
            <span onClick={onClose} style={{ cursor: "pointer", color: THEME.colors.neutral.light.textSecondary, fontSize: "20px" }}>
              ✕
            </span>
          </div>
          <div style={{ display: "flex", gap: "20px", marginTop: "12px", fontSize: "13px" }}>
            <span><b>{user.runs}</b> runs</span>
            <span><b>{user.page_views}</b> page views</span>
            <span style={{ color: user.errors ? THEME.colors.semantic.danger : undefined }}>
              <b>{user.errors}</b> errors
            </span>
            <span style={{ color: THEME.colors.neutral.light.textSecondary }}>
              {rangeLabel}
            </span>
          </div>
        </div>

        <div style={{ padding: "8px 24px 20px", overflowY: "auto" }}>
          <div
            style={{
              fontSize: THEME.typography.caption.size,
              fontWeight: THEME.typography.caption.weight,
              letterSpacing: THEME.typography.caption.tracking,
              textTransform: "uppercase",
              color: THEME.colors.neutral.light.textSecondary,
              padding: "12px 0 4px",
            }}
          >
            Event log
          </div>
          {loading ? (
            <EmptyHint text="Loading event log…" />
          ) : events.length === 0 ? (
            <EmptyHint text="No recorded events in this window." />
          ) : (
            events.map((ev, idx) => {
              const s = eventStyle(ev.event_type)
              return (
                <div
                  key={idx}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: "12px",
                    padding: "9px 0",
                    borderBottom: `1px solid ${THEME.colors.neutral.light.border}`,
                    fontSize: "13px",
                  }}
                >
                  <span>
                    <span style={{ color: s.color, fontWeight: 600, marginRight: "8px" }}>{s.label}</span>
                    <span style={{ fontFamily: THEME.fonts.mono, color: THEME.colors.neutral.light.textPrimary }}>
                      {ev.path ?? "—"}
                    </span>
                    {ev.message && (
                      <span style={{ color: THEME.colors.neutral.light.textSecondary }}> · {ev.message}</span>
                    )}
                  </span>
                  <span style={{ color: THEME.colors.neutral.light.textSecondary, whiteSpace: "nowrap" }}>
                    {new Date(ev.occurred_at).toLocaleString("en-US", {
                      month: "short",
                      day: "numeric",
                      hour: "numeric",
                      minute: "2-digit",
                    })}
                  </span>
                </div>
              )
            })
          )}
        </div>
      </div>
    </div>
  )
}

function EmptyHint({ text }: { text: string }) {
  return (
    <div
      style={{
        padding: "32px 0",
        textAlign: "center",
        fontSize: "13px",
        color: THEME.colors.neutral.light.textSecondary,
      }}
    >
      {text}
    </div>
  )
}

const listRowStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "11px 0",
  borderBottom: `1px solid ${THEME.colors.neutral.light.border}`,
  fontSize: "14px",
  color: THEME.colors.neutral.light.textPrimary,
}

const utilityCell: React.CSSProperties = {
  padding: "10px",
  borderBottom: `1px solid ${THEME.colors.neutral.light.border}`,
  color: THEME.colors.neutral.light.textPrimary,
  verticalAlign: "middle",
}

export default InsightsDashboardPage
